import Delivery from '@/icons/delivery';
import Keys from '@/icons/keys';
import { AspectRatio } from '@/shadcn-ui/aspect-ratio';
import Spacer from '@/common/components/spacer';
import { Breakpoint, useBreakpoint } from '@/common/hooks/use-breakpoint';
import useDoesSupportWebp from '@/common/hooks/use-does-support-webp';
import webpWithPngFallback from '@/common/utils/webp-with-png-fallback';
import { Link } from 'react-router';
import { useMemo } from 'react';
import { withPageContainerStyles } from '@/common/components/page-layout';
import {
  aboutDeliverySupportUrl,
  aboutPickupSupportUrl,
} from '../constants/support-urls';
import { Lot } from '@/schedule/types';

function getMapBackgroundImages(
  cdnUrl: string,
  isWebpSupported: boolean,
  breakpoint: Breakpoint,
  marketSlug: string,
  suffix = ''
) {
  const prefix = `${cdnUrl}/web/images/details/v3/map-${marketSlug}-`;

  switch (breakpoint) {
    case 'mobile':
      return `url(${webpWithPngFallback(`${prefix}mobile${suffix}.webp`, isWebpSupported)})`;
    case 'tablet':
      return `url(${webpWithPngFallback(`${prefix}tablet${suffix}.webp`, isWebpSupported)})`;
    case 'desktop':
      return `url(${webpWithPngFallback(`${prefix}desktop${suffix}.webp`, isWebpSupported)})`;
  }
}

export type VehiclePickupMapProps = {
  marketDisplayName?: string;
  marketSlug: string;
  supportsDelivery?: boolean;
  marketSpecificHub: Lot | undefined;
  cdnUrl: string;
};

export default function VehiclePickupMap({
  marketDisplayName,
  marketSlug,
  supportsDelivery,
  marketSpecificHub,
  cdnUrl,
}: VehiclePickupMapProps) {
  const doesSupportWebp = useDoesSupportWebp();

  const url = useMemo(() => {
    if (marketSpecificHub) {
      // Need the "Flexcar" prefix for Google Maps to select the Flexcar hub building and not select just the address name
      const hubAddress = `Flexcar ${marketSpecificHub.street}, ${marketSpecificHub.city}, ${marketSpecificHub.state} ${marketSpecificHub.postal_code}`;

      // Encode addresses
      const encodedAddress = encodeURIComponent(hubAddress);

      // Construct URL
      let url = `https://www.google.com/maps/search/`;
      url += encodedAddress;

      // Redirect to Google Maps
      return url;
    }

    return '';
  }, [marketSpecificHub]);

  const breakpoint = useBreakpoint();

  const mapAspectRatio = useMemo(() => {
    switch (breakpoint) {
      case 'mobile':
        return 328 / 372;

      case 'tablet':
        return 326 / 496;

      case 'desktop':
        return 630 / 304;
    }
  }, [breakpoint]);

  const handleMapClick = () => {
    window.open(url, '_blank');
  };

  return (
    <div className={withPageContainerStyles('mx-auto my-0 max-w-[1440px] py-[80px]')}>
      <div className="flex flex-col items-center">
        <h1>
          {supportsDelivery
            ? `Easy pickup or delivery in ${marketDisplayName}`
            : `Flexible pickup in ${marketDisplayName ?? 'New England'}`}
        </h1>
        <Spacer variant="height16" />
        <div className="flex-1">
          <p className="body">
            {supportsDelivery
              ? 'In as early as two days, your Flexcar will be ready, either for pickup or delivered directly to your door. We handle every detail, ensuring a well-prepared vehicle for your convenience. Order today for an easy start with Flexcar.'
              : 'In as early as two days, your Flexcar will be ready for pickup. We handle every detail, ensuring a well-prepared vehicle for your convenience. Order today for an easy start with Flexcar.'}
          </p>
        </div>
      </div>
      <Spacer variant="height32" />
      <div className="flex flex-col md:flex-row-reverse">
        <div className="min-w-full max-w-full md:min-w-[50%] md:max-w-[324px] lg:max-w-[628px]">
          <div
            onClick={handleMapClick}
            role="button"
            tabIndex={0}
            onKeyPress={handleMapClick}
          >
            <AspectRatio
              ratio={mapAspectRatio}
              className="ml-0 bg-contain bg-center bg-no-repeat md:mb-0 md:ml-[24px]"
              style={{
                backgroundImage: getMapBackgroundImages(
                  cdnUrl,
                  doesSupportWebp,
                  breakpoint,
                  marketSlug,
                  '@3x'
                ),
              }}
            />
          </div>
        </div>
        <Spacer variant="height32" />
        {supportsDelivery ? (
          <div className="flex-1">
            <div className="flex flex-row items-start gap-[24px]">
              <div className="flex w-[35px] flex-shrink-0 flex-col items-start justify-center">
                <Keys />
              </div>
              <div className="grid">
                <p className="body mx-0 my-auto pr-[5px]">
                  <b>For pickup:</b>
                </p>

                <Spacer variant="height8" />
                <p className="body pr-[5px]">
                  Choose a time that works best for you. Our concierge will be there to
                  introduce you to your new Flexcar, and address any questions to ensure a
                  smooth start to your journey.
                </p>
                <Spacer variant="height16" />
                <Link
                  to={aboutPickupSupportUrl}
                  target="_blank"
                  className="link-body inline-block"
                  rel="noreferrer"
                >
                  Learn more about pickup
                </Link>
              </div>
            </div>
            <Spacer variant="height32" />
            <div className="flex flex-row items-start gap-[24px]">
              <div className="flex w-[35px] flex-shrink-0 flex-col items-start">
                <Delivery />
              </div>
              <div>
                <p className="body mx-0 my-auto pr-[5px]">
                  <b>For Delivery:</b>
                </p>

                <Spacer variant="height8" />
                <p className="body pr-[5px]">
                  Pick a time that works best for you, and let our concierge do the rest.
                  They&apos;ll bring your new Flexcar to your chosen location, guide you
                  through all the essentials, and ensure any questions you have are
                  thoroughly addressed.
                </p>
                <Spacer variant="height16" />
                <Link
                  to={aboutDeliverySupportUrl}
                  target="_blank"
                  className="link-body inline-block"
                  rel="noreferrer"
                >
                  Learn more about delivery
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1">
            <Spacer variant="height16" />
            <div className="flex flex-row items-start gap-[24px]">
              <div className="flex w-[35px] flex-shrink-0 flex-col items-start justify-center">
                <Keys />
              </div>
              <div className="grid">
                <p className="body mx-0 my-auto pr-[5px]">
                  <b>For pickup:</b>
                </p>

                <Spacer variant="height8" />
                <p className="body pr-[5px]">
                  {marketDisplayName === 'Nashville'
                    ? 'Choose a time that works best for you. Our concierge will be there to introduce you to your new Flexcar, and address any questions to ensure a smooth start to your journey.'
                    : 'Choose a time that works best for you. Our concierge will be there to introduce you to your new Flexcar, and address any questions to ensure a smooth start to your journey.'}
                </p>
                <Spacer variant="height16" />
                <Link
                  to={aboutPickupSupportUrl}
                  target="_blank"
                  className="link-body inline-block"
                  rel="noreferrer"
                >
                  Learn more about pickup
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
